import React, { useState } from "react";
import {
    TextField,
    Typography,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import logoImg from "../../shared/svg/AppLogo/management.png";
import { authService } from "../../services/authService";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
    ForgotPasswordContainer,
    ForgotPasswordWrapper,
    LogoContainer,
    FormTitle,
    FormDescription,
    ButtonsContainer,
    BackButton,
    SubmitButton,
} from "./Styled";

type ChangePasswordFormData = {
    username: string;
    old_password: string;
    new_password: string;
    new_password_confirm: string;
};

const ChangePasswordPage = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<ChangePasswordFormData>({
        username: "",
        old_password: "",
        new_password: "",
        new_password_confirm: "",
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const toggleOldPasswordVisibility = () => {
        setShowOldPassword(!showOldPassword);
    };

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError("");
        setIsLoading(true);

        if (formData.new_password !== formData.new_password_confirm) {
            setError("Mật khẩu xác nhận không trùng khớp.");
            setIsLoading(false);
            return;
        }

        try {
            await authService.changePassword(formData);
            setSuccess("Đổi mật khẩu thành công");
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate("/login");
            }, 2000);
        } catch (err: any) {
            if (err.password) {
                // Handle specific field errors
                const errorData = err.password;
                setError(errorData);
            } else {
                setError("Đã xảy ra lỗi. Vui lòng thử lại sau.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate("/login");
    };

    return (
        <ForgotPasswordContainer maxWidth={false}>
            <ForgotPasswordWrapper elevation={3}>
                <LogoContainer>
                    <img src={logoImg} alt="Logo" />
                    <Typography variant="h5" ml={1} fontWeight="700" fontSize="32px">
                        PMS
                    </Typography>
                </LogoContainer>

                <form onSubmit={handleSubmit}>
                    <FormTitle variant="h6" align="center">Đổi mật khẩu</FormTitle>

                    <FormDescription variant="body2">
                        - Mật khẩu chứa ít nhất 8 ký tự.
                    </FormDescription>

                    <FormDescription variant="body2">
                        - Mật khẩu bao gồm chữ cái in hoa, chữ cái thường, số và ký tự đặc biệt.
                    </FormDescription>

                    <FormDescription variant="body2" mb={4}>
                        - Không sử dụng lại 3 mật khẩu trước đó.
                    </FormDescription>

                    <TextField
                        fullWidth
                        label="Tên đăng nhập"
                        name="username"
                        variant="outlined"
                        value={formData.username}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                        sx={{
                            mb: 3,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                            },
                            "& .MuiFormLabel-asterisk": {
                                color: "#d32f2f",
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Mật khẩu cũ"
                        name="old_password"
                        type={showOldPassword ? "text" : "password"}
                        variant="outlined"
                        value={formData.old_password}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                        sx={{
                            mb: 3,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                            },
                            "& .MuiFormLabel-asterisk": {
                                color: "#d32f2f",
                            },
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={toggleOldPasswordVisibility}
                                        edge="end"
                                    >
                                        {showOldPassword ? (
                                            <VisibilityOffIcon />
                                        ) : (
                                            <VisibilityIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Mật khẩu mới"
                        name="new_password"
                        type={showNewPassword ? "text" : "password"}
                        variant="outlined"
                        value={formData.new_password}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                        sx={{
                            mb: 3,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                            },
                            "& .MuiFormLabel-asterisk": {
                                color: "#d32f2f",
                            },
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={toggleNewPasswordVisibility}
                                        edge="end"
                                    >
                                        {showNewPassword ? (
                                            <VisibilityOffIcon />
                                        ) : (
                                            <VisibilityIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Xác nhận mật khẩu"
                        name="new_password_confirm"
                        type={showConfirmPassword ? "text" : "password"}
                        variant="outlined"
                        value={formData.new_password_confirm}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                        sx={{
                            mb: 3,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                            },
                            "& .MuiFormLabel-asterisk": {
                                color: "#d32f2f",
                            },
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={toggleConfirmPasswordVisibility}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? (
                                            <VisibilityOffIcon />
                                        ) : (
                                            <VisibilityIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {success && (
                        <Alert
                            severity="success"
                            sx={{
                                mb: 2,
                            }}
                        >
                            {success}
                        </Alert>
                    )}

                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 2,
                                "& .MuiAlert-message": {
                                    color: "#d32f2f",
                                    fontSize: "14px",
                                },
                                backgroundColor: "#fdeded",
                                padding: "6px 16px",
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <ButtonsContainer>
                        <BackButton
                            variant="contained"
                            onClick={handleBackToLogin}
                            disabled={isLoading}
                        >
                            Trở lại
                        </BackButton>
                        <SubmitButton type="submit" variant="contained" disabled={isLoading}>
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                "Đổi mật khẩu"
                            )}
                        </SubmitButton>
                    </ButtonsContainer>
                </form>
            </ForgotPasswordWrapper>
        </ForgotPasswordContainer>
    );
};

export default ChangePasswordPage;
